import React from "react";
import type { ReactNode } from "react";

import { DOMNode } from "html-react-parser";
import parse from "html-react-parser";

import { extractFigureContent } from "../utils/extract-figure-content";
import type { Element } from "../utils/type";

/**
 * html-transform 에서 사용하는 transform factory 타입
 * (순수 타입 선언만 하여 순환 의존성을 피한다)
 */
export type HtmlTransformFactory = (
  elementData: Element,
  index: number,
) => (
  reactNode: ReactNode,
  domNode: DOMNode,
  nodeIndex: number,
) => React.ReactElement | null | undefined;

/**
 * chart category를 렌더링
 * - figure 안에 base64 이미지, figcaption, table 이 포함된 구조를 처리한다.
 * - table 은 여러 개가 올 수 있으므로(예: 계열별로 쪼개진 차트 데이터) 문서 순서대로 모두 렌더한다.
 */
export function renderChart(
  html: string,
  elementData: Element,
  index: number,
  createTransform: HtmlTransformFactory,
): React.ReactElement {
  const { figcaption, tables } = extractFigureContent(html);

  return (
    // 연속한 table 사이에는 16px(mt-4) 간격을 준다 — table 스타일에는 세로 여백이 없어 그대로 붙는다.
    // table 을 div 로 감싸면 flex 자식이 바뀌어 표 너비가 달라지므로 부모에서 셀렉터로 준다.
    <figure className="flex flex-col p-2 [&>table+table]:mt-4 [&_figcaption]:flex-col">
      <img
        src={`data:image/jpg;base64,${elementData.base64_encoding}`}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-auto max-w-full rounded-lg"
      />
      {figcaption &&
        parse(figcaption, {
          transform: createTransform(
            {
              ...elementData,
              content: { ...elementData.content, html: figcaption },
              category: "figcaption",
            },
            index,
          ),
        })}
      {tables.map((table, tableIndex) => (
        <React.Fragment key={`chart-table-${index}-${tableIndex}`}>
          {parse(table, {
            transform: createTransform(
              {
                ...elementData,
                content: { ...elementData.content, html: table },
                category: "table",
              },
              index,
            ),
          })}
        </React.Fragment>
      ))}
    </figure>
  );
}
